<template>
    <div class="details">
        <ProductDetailsHeader :product="product" />

        <Divider color="lightGrey" bottomMargin="xl" topMargin="m" thickness="1" />

        <div class="details-journey">
            <Journeys
                class="journeys"
                type="Ferry"
                direction="column"
                :legs="product.Legs"
                :showDetailed="false"
                :showHeaderBackground="false"
                :showHeader="product.Legs.length > 1" />
        </div>

        <div class="details-actions">
            <ButtonLink size="m" color="black" :onClick="onFerryDetailsClick">
                {{ t({ key: 'shared.ferry_details' }) }}
            </ButtonLink>

            <ButtonLink
                v-if="description && description !== ''"
                size="m"
                color="black"
                :onClick="onFerryTermsClick">
                {{ t({ key: 'shared.ticket_policy' }) }}
            </ButtonLink>
        </div>

        <Divider v-if="!isDesktop" color="lightGrey" topMargin="xl" thickness="1" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed } from 'vue';
    import { useStore } from '@/store';
    import { ProductModalTab, ModalType } from '@/store/general/types';
    import { t } from '@shared/helpers/translation';
    import { FerryProduct } from '@/api/Cart/cart.interfaces';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { useScreenSize } from '@shared/helpers/viewport';
    import {
        logStatsigCheckoutMoreInformationOpen,
        logStatsigCheckoutTicketPolicyOpen,
    } from '@/tracking/statsig/checkout.functions';
    import Journeys from '@/components/shared/Journey/Journeys.vue';
    import ProductDetailsHeader from '@/components/checkoutPageV3/Shared/ProductDetailsHeader/ProductDetailsHeader.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';

    export default defineComponent({
        name: 'FerryDetails',
        methods: { t, getMoneyObjectAsString },
        components: {
            ButtonLink,
            Divider,
            ProductDetailsHeader,
            Journeys,
        },
        props: {
            product: {
                type: Object as PropType<FerryProduct>,
                required: true,
            },
        },
        setup(props) {
            const { isDesktop } = useScreenSize();
            const store = useStore();

            function onFerryDetailsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Details,
                });

                logStatsigCheckoutMoreInformationOpen(props.product.Type);
            }

            function onFerryTermsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Terms,
                });

                logStatsigCheckoutTicketPolicyOpen(props.product.Type);
            }

            const description = computed<string | null>(() => {
                const ferry = props.product?.Legs?.[0];
                const segments = ferry?.Segments;

                if (Array.isArray(segments) && segments.length > 0) {
                    const lastSegment = segments[segments.length - 1];
                    return lastSegment?.TicketType?.Description ?? null;
                }

                return null;
            });

            return {
                onFerryDetailsClick,
                onFerryTermsClick,
                description,
                isDesktop,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Details.scss';
</style>
